// src/locales/en.ts

// Top-level auth & common strings
const en = {
  // 🔒 ForgotPassword.tsx
  enterYourEmail: "Enter your email",
  sendResetLink: "Send Reset Link",
  pleaseEnterEmail: "❌ Please enter your email.",
  couldNotSendResetEmail: "❌ Could not send reset email. Try again.",

  // 🌟 Start page / login
  slogan: "Let's get the party started",

email: "Email",
password: "Password",
login: "Log In",
createAccount: "Create new account",
forgotPassword: "Forgot Password?",

startPage: {
  loginSuccess: "✅ Login successful!",
  invalidCredentials: "❌ Invalid login credentials!",
  privacyTerms: "Privacy & Terms",
},

twoFA: {
  title: "Enter the 6-digit code",
  intro: "We emailed a login verification code to you.",
  placeholder: "••••••",
  cancel: "Cancel",
  verify: "Verify & Continue",
  verifying: "Verifying…",
  hint: "Didn’t get it? It can take a minute. Also check spam.",
  missingToken: "Missing 2FA token",
  invalidCode: "Enter the 6-digit code we sent.",
  verifyFailed: "Verification failed.",
  requestFailed: "Could not send verification code.",
},     

  // 👤 Profile setup / signup
  profileSetup: {
    title: "Create Your Bebo Profile",
    profileName: "Profile Name",
    email: "Email",
    password: "Password",
    over18: "I confirm that I am over 18 years old",
    createProfile: "Create Profile",
    alreadyAccount: "Already have an account?",
  },

  // 🔁 Reset password flow
  resetPassword: {
    title: "Reset Your Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    resetting: "Resetting...",
    resetButton: "Reset Password",
    passwordsMismatch: "Passwords do not match!",
    resetError: "❌ Error resetting password.",
    success: "✅ Password reset successful! Logging you in...",
  },

  // ✉️ Confirm email (email content + screen copy)
  confirmEmail: {
    title: "Check your email to confirm",
    subtitle: "We've sent a confirmation email to:",
    note: "Please check your inbox and spam folder.",
    returnButton: "Back to login",
    heading: "Welcome to Bebo 🎉",
    intro: "Click the button below to confirm your email and activate your profile:",
    button: "Confirm Email",
    footer1: "This link is valid for 24 hours for your security.",
    footer2: "If you did not create this account, simply ignore this message.",
    footer3: "This email was sent to you by Bebo.",
  },

  // 📩 “Check your email” screen
  checkEmail: {
    title: "Confirm Your Email",
    subtitle: "We've sent a confirmation link to:",
    note: "Please check your inbox (and spam folder) to complete registration.",
    returnButton: "Return to Login",
  },

  // 📤 “Reset link sent” screen
  emailSentTitle: "Check Your Email",
  emailSentSubtitle: "We've sent a password reset link to:",
  emailSentNote: "Please check your inbox (and spam folder).",
  emailSentReturnButton: "Return to Start Page",

  // 🌐 Language
  language: "Language",
  
  common: {
    back: "Back",
    close: "Close",
  },

    // GalleryPosting (create / edit gallery post)
galleryPosting: {
  overlay: {
    uploading: "Uploading",
  },

  // General alerts & confirmations
  alerts: {
    mediaPerPostLimit:
      "You can add up to 8 media per post. Remove something before adding more.",
    mediaPerPostHardLimit:
      "You can only have up to 8 media in a single post.",
    videosSkippedTooLongShort:
      "We skipped video(s) longer than 3 minutes and 3 seconds.",
    videosSkippedTooLongSummary:
      "Some videos were skipped because they are longer than 3 minutes and 3 seconds.",
    filesSkippedByCapSummary:
      "Some files were skipped because you hit the limit of 8 media per post.",

    loginRequired: "You need to Login.",

    postTooLargeClient:
      "This post is too large ({{actualGb}} GB). The maximum allowed per post is about {{maxGb}} GB.\n\nPlease remove some videos or shorten them before posting.",
    postTooLargeServer:
      "Your post is too large. The maximum total media size per post is about {{maxGb}} GB.\n\nPlease remove some media or shorten your videos.",

    dailyPostLimitReached:
      "You have reached your daily posting limit. Try again tomorrow.",

    vpnBlockedFallback:
      "Media uploads are not allowed while using a VPN or proxy. Please disable it and try again.",

    postFailed: "Failed to post media.",
    discardConfirm: "Are you sure you want to discard changes?",
  },

  banner: {
    vpnBlocked: "Media uploads are not allowed while using a VPN or proxy. Please disable it and try again.",
  },

  buttons: {
    delete: "Delete",
    filter: "🎛️ Filter",
    music: "🎵 Music",
  },

  fallback: {
    noPreview: "No preview available",
  },
},

  // Gallery
  gallery: {
    noPostsForFilter: "No posts available for the selected filter.",
    unknownUser: "Unknown User",
    unknownLocation: "Unknown",

    post: {
      unknownUser: "Unknown user",
      noMedia: "No media available",
      postedAt: "posted at {{date}}",
      presentationButton: "Presentation",
    },
    
    edit: {
      missingPostId: "Missing post id",
      saveFailed: "Failed to save changes.",
      discardConfirm: "Discard all changes?",
      saving: "Saving",
      anonymous: "Anonymous",
      noPreview: "No preview available",
      delete: "Delete",
    },

    media: {
      previewAlt: "Preview",
    },

    addMusic: {
    back: "Back",
    heading: "🎵 Choose Music",
    searchPlaceholder: "Search title or artist…",
    clear: "Clear",
    noMatches: "No tracks match “{{query}}”.",
    openDetails: "Open details for {{title}}",
    choose: "Choose",
    chooseTitle: "Choose this track",
    noVideo: "No video selected",
    unknownTrack: "track",
  },

  chooseTrack: {
    selectTrack: "Select a track.",
    noVideoSource:
      "No HTTP video source and no File available. Re-open from Add Music so the video file is in memory, or provide a server URL.",
    previewFailed: "Preview failed. See console for details.",
    generatePreviewFirst: "Generate a preview first.",
    saveFailed: "Save failed. See console for details.",

    trimMusicTitle: "Trim music",
    unknownArtist: "Unknown artist",
    trackPreview: "Track preview",

    play: "Play",
    pause: "Pause",

    mergeCta: "Merge Music & Video",
    saveCta: "Save & Continue",

    processing: "Processing on server…",
    noVideo: "No video selected",
  },
  },

  videoFilter: {
    sidebar: {
      title: "Filter",
    },
    buttons: {
      reset: "Reset",
      save: "Save",
      back: "Back",
      processing: "Processing…",
    },
    messages: {
      noMedia: "No media found for editing.",
      noSource: "No video source…",
      processFailed: "Failed to process the video. Check console for details.",
    },
    preview: {
      placeholderAria: "Video placeholder",
    },
    controls: {
      muted: "Muted",
      autoplay: "Autoplay",
    },
  },

  locationChange: {
    title: "Change Location",
    sidebar: {
      title: "Search city or country",
    },
    search: {
      placeholder: "Type a city or country…",
      clearAria: "Clear search",
      searching: "Searching…",
      noResults: "No results",
      ariaSuggestions: "Location suggestions",
    },
    badge: {    
      country: "Country",
      state: "State/Region",
      city: "City",
      village: "Village",
      suburb: "Suburb",
      place: "Place",
    },
    actions: {
      useCurrentLocation: "Use Current Location",
      confirm: "Confirm Location",
      closeAria: "Close",
    },
    labels: {
      selected: "Selected: {{city}}, {{country}}",
    },
  },

  confirmLocation: {
    title: "Confirm your location",
    loading: "Loading map and location data...",
    locationKnown: "{{city}} ({{country}})",
    locationUnknown: "Location unknown",
    captionPlaceholder: "Write a caption (optional)…",
    buttons: {
      confirmAndPost: "Confirm Location & Post",
      posting: "Posting…",
    },
    alerts: {
      missingMediaOrLocation: "Missing media or location. Please check and try again.",
      loginRequired: "You need to log in.",
      postSuccess: "Your post was published successfully!",
      postFailed: "Failed to post. Please try again.",
    },
  },     

  followers: {
    title: "People Following You",
    search: {
      placeholder: "Search followers…",
      ariaLabel: "Search followers",
    },
    states: {
      loading: "Loading…",
      noMatches: "No matches.",
      noFollowers: "No followers yet.",
    },
    row: {
      followedYou: "Followed you:",
      noDate: "—",
    },
    buttons: {
      view: "View",
      moreActions: "More actions",
    },
    dropdown: {
      block: "Block",
      blockAndReport: "Block & Report",
    },
    block: {
      displayNameFallback: "this user",
      confirm:
        "Block {{displayName}}? They won't be able to interact with you, and you won't see each other’s posts or messages.",
      success: "User blocked.",
      failed: "Block failed",
    },
  },

  followingList: {
    title: "People You Follow",
    searchPlaceholder: "Search profiles…",
    searchAria: "Search profiles",
    loading: "Loading…",
    emptyNoQuery: "You’re not following anyone yet.",
    emptyWithQuery: "No matches.",
    followedOn: "Followed on: {{date}}",
    followedOnUnknown: "Followed on: —",
    viewProfile: "View",
  
    blockConfirm:
      "Block {{name}}? They won't be able to interact with you, and you won't see each other’s posts or messages.",
    blockUnnamedUser: "this user",
    blockSuccess: "User blocked.",
    blockFailedFallback: "Block failed",
    moreActions: "More actions",
    block: "Block",
    blockAndReport: "Block & Report",
  },
  

  followingFeed: {
    noPosts: "No posts yet",
  },

  //Edit Media
  editMedia: {
    title: "Edit Media",
    noItem: "No media item selected for editing.",
    save: "Save",
    alt: "Edit Media",
  },

  imageFilter: {
    sidebar: {
      title: "Filter",
    },
    buttons: {
      reset: "Reset",
      save: "Save",
      back: "Back",
    },
    messages: {
      noMedia: "No media found for editing.",
      loading: "Loading…",
    },
  },

  posts: {
    create: {
      mediaReady: "Media attached and ready to save.",
      save: "Save",
    },
  },

  // FollowButtonInline
  followButton: {
    follow: "Follow",
    unfollow: "Unfollow",
  },      

  //Hashtag Selector
  hashtagSelector: {
    placeholder: "Type or choose hidden hashtags",
  },

  // Language Selector
  languageSelector: {
    title: "Select Your Language",
    close: "Close",
  },
//Lazymedia
  lazymedia: {
    videoNotSupported: "Your browser does not support the video tag.",
  },

  //Mediauploader
  mediaUploader: {
    uploading: "Uploading…",
    uploadFailed: "Upload failed",
    uploadedLabel: "Uploaded:",
    openFile: "open file",
  },

// NotificationSign
  notifications: {
    open: "Open notifications",
  },    

  //FirstRunPrivacy
  firstRunPrivacy: {
    title: "Let’s set your privacy basics",
    loading: "Loading…",
    errorSave: "Could not save preferences.",
    yes: "Yes",
    no: "No",
    continue: "Continue",
    saving: "Saving…",
    footerNote: "You can change these anytime in Settings → Data & Privacy.",
    toggles: {
      locationTitle: "Use device location",
      locationDesc: "Use my device location when I tag a post or explore nearby.",
      analyticsTitle: "Anonymous analytics",
      analyticsDesc: "Help us improve features in aggregate. No ads, no trackers.",
      pushTitle: "Push notifications",
      pushDesc: "Allow app notifications for messages and activity.",
    },
  },

  requirePrivacyChange: {
    title: "For posting, location usage needs to be activated",
    close: "Close",
    errorSave: "Could not save preferences.",
  },

  requirePrivacyChangeCL: {
    title: "To use current location, location access needs to be enabled",
    close: "Close",
    errorSave: "Could not save preferences.",
  },

  requirePrivacyChangeMap: {
    title: "To use current location on the map, location access needs to be enabled",
    close: "Close",
    cancel: "Cancel",
    errorSave: "Could not save preferences.",
  },

//Chat
chat: {
  // --- deletes / confirmations ---
  deleteMessage: {
    everyone: "Delete this message for everyone?",
    me: "Delete this message for you?",
  },

  deletedUser: "Deleted User",

  deleteConversation: {
    deletedUser:
      "Permanently delete this conversation? All messages and media will be removed and cannot be recovered.",
    // {{name}} is optional – you already pass empty string if unknown
    normal:
      "Delete this conversation with {{name}}? The other participant will still keep their copy (messages, media and profile name).",
  },

  // --- E2EE / errors ---
  e2eeInitializing: "Secure chat is still initializing on this device.",
  // {{msg}} = API error message from backend
  sendFailed: "Failed to send: {{msg}}",

  // --- sidebar / preview labels ---
  preview: {
    message: "Message",
    photo: "Photo",
    video: "Video",
    audio: "Audio",
    media: "Media",
  },

  // --- header / profile bits ---
  openVisitorProfile: "Open visitor profile",
  fallbackUser: "User",
  unknownUser: "Unknown User",

  // --- composer / buttons / hints ---
  addMedia: "Add media",
  pickChatFirst: "Pick a chat first",
  replying: "Replying",
  placeholder: "Type a message…",
  emoji: "Emoji",
  send: "Send",    

  // --- empty / helper text ---
  selectUserHint: "Select a user on the left to start chatting.",

  upload: {
    headerTitle: "Send media to {{name}}",
    genericUser: "User",

    back: "Back",

    addButton: "Add photos/videos",
    addButtonTitleReady: "Pick photos & videos",
    addButtonTitleInit: "Secure chat is initializing…",

    dropTitle: "Click or drop files here",
    dropHelp: "Click to pick images/videos or drop them here",

    ttlTitle: "How long this media should remain available after it’s viewed",
    ttl: {
      choose: "Choose expiry…",
      afterView: "Immediately after view",
      min1: "1 minute",
      min5: "5 minutes",
      hour1: "1 hour",
      hours24: "24 hours",
      days3: "3 days",
      days30: "30 days",
    },

    sendCta: "Send {{count}} & go →",

    kindImage: "Image",
    kindVideo: "Video",

    remove: "Remove",
  },
  message: {
    media: {
      expired: "Expired",
      expiredTitle: "Media expired",
      show: "Show media",
      showWithCount: "Show media ({{count}})",
    },
    menu: {
      optionsTitle: "Message options",
      copy: "Copy",
      reply: "Reply",
      delete: "Delete message",
    },
    quoted: {
      unknownUser: "User",
    },
  },
},

//ChatMediaPresentation
chatMedia: {
  videoTitle: 'Video',
  noMedia: 'No media to show.',
},

profileActions: {
  moreActions: "More actions",

  postsNamed: "{{name}}'s posts",
  postsGeneric: "View posts",
  thisUser: "this user",

  follow: "Follow",
  unfollow: "Unfollow",

  block: "Block",
  unblock: "Unblock",
  blockAndReport: "Block & report",

  blockConfirm:
    "Block {{name}}? They won't be able to interact with you, and you won't see each other’s posts or messages.",

  blocked: "User blocked.",
  unblocked: "User unblocked.",
  blockedWithInfo:
    "User blocked. You can report them from the Report screen later.",

  errors: {
    actionFailed: "Action failed",
    blockFailed: "Block failed",
    unblockFailed: "Unblock failed",
  },
},


  // 🔗 Email-confirm status messages
  confirmEmailLoading: "⏳ Confirming your email...",
  confirmEmailSuccess: "🎉 Your email has been confirmed! Redirecting...",
  confirmEmailError: "❌ Invalid or expired confirmation link.",

  emailConfirmedTitle: "✅ Email confirmed successfully!",
  emailConfirmedRedirect: "Redirecting to your profile...",
  emailConfirmedFailed: "❌ Confirmation failed",
  emailConfirmedMissingToken: "Missing or invalid confirmation token.",
  emailConfirmedChecking: "Verifying your confirmation link...",
} as const;

export default en;
